<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { formatDuration, formatPhone } from 'dashboard/helper/relayVoicePhone';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import {
  callerMessageLink,
  getMissedCalls,
  updateMissedCall,
} from 'dashboard/api/relayVoiceInbound';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import RecordingPlayer from 'dashboard/components-next/call/RecordingPlayer.vue';

// The team's callback queue: inbound calls nobody answered (after hours,
// after the maximum wait, or callers who hung up). Any agent claims one and
// marks it called back; team leads and administrators assign and reopen.
const props = defineProps({
  accountId: { type: [Number, String], required: true },
  agents: { type: Array, default: () => [] },
  canCall: { type: Boolean, default: false },
});
const emit = defineEmits(['callBack', 'changed']);
const { t } = useI18n();

const FILTERS = ['open', 'mine', 'called_back', 'all'];
const PER_PAGE = 25;
const filter = ref('open');
const rows = ref([]);
const total = ref(0);
const page = ref(1);
const canAssign = ref(false);
const userId = ref(null);
const loading = ref(false);
const loadError = ref('');
const working = ref('');
const playing = ref('');

const when = row =>
  messageTimestamp(Date.parse(row.called_at) / 1000, 'MMM d, h:mm a');
const caller = row =>
  row.contact?.name ||
  row.caller_name ||
  formatPhone(row.number) ||
  t('RELAY_VOICE.UNKNOWN_CALLER');
const isPhone = row => /^\+?\d{8,15}$/.test(row.number || '');
const mine = row => row.assignee?.id === userId.value;
const conversationLink = row =>
  frontendURL(
    conversationUrl({ accountId: props.accountId, id: row.conversation_id })
  );
const statusClass = status =>
  ({
    open: 'bg-n-ruby-3 text-n-ruby-11',
    claimed: 'bg-n-amber-3 text-n-amber-11',
    called_back: 'bg-n-teal-3 text-n-teal-11',
  })[status];
const empty = computed(() => !loading.value && !rows.value.length);

let revision = 0;
const load = async () => {
  revision += 1;
  const current = revision;
  loading.value = true;
  loadError.value = '';
  try {
    const result = await getMissedCalls(props.accountId, {
      status: filter.value,
      page: page.value,
    });
    if (current !== revision) return;
    rows.value = result.data;
    total.value = result.meta.total;
    canAssign.value = result.can_assign;
    userId.value = result.user_id;
  } catch {
    if (current !== revision) return;
    rows.value = [];
    loadError.value = t('RELAY_VOICE.MISSED.LOAD_ERROR');
  } finally {
    if (current === revision) loading.value = false;
  }
};

const act = async (row, action, body = {}) => {
  working.value = `${row.call_id}:${action}`;
  try {
    const result = await updateMissedCall(
      props.accountId,
      row.call_id,
      action,
      body
    );
    Object.assign(row, result.data);
    emit('changed');
    if (filter.value !== 'all') load();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('RELAY_VOICE.MISSED.ACTION_ERROR')
    );
  } finally {
    working.value = '';
  }
};

const callBack = row => {
  if (row.status === 'open') act(row, 'claim');
  emit('callBack', row.number);
};
const messageLink = row => download =>
  callerMessageLink(props.accountId, row.call_id, download);

watch(filter, () => {
  page.value = 1;
  load();
});
watch(page, load);
onMounted(load);
defineExpose({ load });
</script>

<template>
  <section class="flex flex-col gap-4" data-tour="calls-missed">
    <div class="flex flex-wrap items-center gap-2" role="radiogroup">
      <button
        v-for="option in FILTERS"
        :key="option"
        type="button"
        role="radio"
        :aria-checked="filter === option"
        class="px-3 py-1 rounded-full text-xs font-medium border transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
        :class="
          filter === option
            ? 'bg-n-slate-12 text-n-solid-1 border-n-slate-12'
            : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
        "
        @click="filter = option"
      >
        {{ t(`RELAY_VOICE.MISSED.FILTERS.${option.toUpperCase()}`) }}
      </button>
      <span class="text-xs text-n-slate-10 ms-auto">
        {{ t('RELAY_VOICE.MISSED.HINT') }}
      </span>
    </div>

    <div v-if="loading && !rows.length" class="flex justify-center py-16">
      <Spinner />
    </div>
    <p
      v-else-if="loadError"
      role="alert"
      class="py-16 text-center text-sm text-n-ruby-11"
    >
      {{ loadError }}
    </p>
    <div
      v-else-if="empty"
      class="py-16 flex flex-col items-center gap-2 text-center"
    >
      <span
        class="i-ph-phone-check-bold size-8 text-n-teal-9"
        aria-hidden="true"
      />
      <p class="text-sm text-n-slate-11">
        {{ t(`RELAY_VOICE.MISSED.EMPTY_${filter.toUpperCase()}`) }}
      </p>
    </div>

    <ul v-else class="flex flex-col gap-2">
      <li
        v-for="row in rows"
        :key="row.call_id"
        class="rounded-xl border border-n-weak bg-n-solid-1 p-4 flex flex-col gap-3"
        data-test-id="missed-call"
      >
        <div class="flex items-start gap-3">
          <span
            class="flex items-center justify-center size-9 rounded-full shrink-0"
            :class="
              row.after_hours
                ? 'bg-n-iris-3 text-n-iris-11'
                : 'bg-n-ruby-3 text-n-ruby-11'
            "
          >
            <span
              :class="row.after_hours ? 'i-ph-moon-bold' : 'i-ph-phone-x-bold'"
              class="size-4"
              aria-hidden="true"
            />
          </span>
          <div class="flex-1 min-w-0">
            <p class="font-medium text-n-slate-12 truncate">
              {{ caller(row) }}
            </p>
            <p class="text-xs text-n-slate-10 tabular-nums">
              {{ formatPhone(row.number) }} · {{ when(row) }}
              <template v-if="row.wait_seconds">
                ·
                {{
                  t('RELAY_VOICE.MISSED.WAITED', {
                    time: formatDuration(row.wait_seconds),
                  })
                }}
              </template>
            </p>
            <div class="flex flex-wrap gap-1.5 mt-2">
              <span
                class="px-2 py-0.5 rounded-full text-[0.6875rem] font-medium"
                :class="statusClass(row.status)"
              >
                {{
                  row.status === 'claimed' && row.assignee
                    ? t('RELAY_VOICE.MISSED.CLAIMED_BY', {
                        name: mine(row)
                          ? t('RELAY_VOICE.MISSED.YOU')
                          : row.assignee.name,
                      })
                    : t(`RELAY_VOICE.MISSED.STATUS.${row.status.toUpperCase()}`)
                }}
              </span>
              <span
                v-if="row.after_hours"
                class="px-2 py-0.5 rounded-full text-[0.6875rem] font-medium bg-n-iris-3 text-n-iris-11"
              >
                {{ t('RELAY_VOICE.MISSED.AFTER_HOURS') }}
              </span>
              <span
                v-if="row.voice_message_id"
                class="px-2 py-0.5 rounded-full text-[0.6875rem] font-medium bg-n-alpha-2 text-n-slate-11"
              >
                {{ t('RELAY_VOICE.MISSED.LEFT_MESSAGE') }}
              </span>
              <span
                v-if="row.status === 'called_back'"
                class="text-[0.6875rem] text-n-slate-10"
              >
                {{
                  t('RELAY_VOICE.MISSED.CALLED_BACK_BY', {
                    name: row.called_back_by,
                  })
                }}
              </span>
            </div>
          </div>
          <div class="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
            <a
              v-if="row.conversation_id"
              :href="conversationLink(row)"
              class="text-xs font-medium text-n-brand hover:underline px-2"
            >
              {{ t('RELAY_VOICE.MISSED.CONVERSATION') }}
            </a>
            <Button
              v-if="row.voice_message_id"
              :label="t('RELAY_VOICE.MISSED.MESSAGE')"
              icon="i-ph-play-fill"
              size="xs"
              variant="faded"
              color="slate"
              @click="playing = playing === row.call_id ? '' : row.call_id"
            />
            <Button
              v-if="canCall && isPhone(row) && row.status !== 'called_back'"
              :label="t('RELAY_VOICE.CALLS.CALL_BACK')"
              icon="i-ph-phone-bold"
              size="xs"
              color="teal"
              @click="callBack(row)"
            />
          </div>
        </div>

        <RecordingPlayer
          v-if="playing === row.call_id"
          :fetch-link="messageLink(row)"
          :label="
            t(
              `RELAY_VOICE.RECORDING.MESSAGE_${row.after_hours ? 'AFTER_HOURS' : 'MISSED'}`
            )
          "
        />

        <div
          class="flex flex-wrap items-center gap-2 border-t border-n-weak pt-3"
        >
          <Button
            v-if="row.status === 'open'"
            :label="t('RELAY_VOICE.MISSED.CLAIM')"
            size="xs"
            variant="faded"
            :is-loading="working === `${row.call_id}:claim`"
            @click="act(row, 'claim')"
          />
          <Button
            v-if="
              row.status !== 'called_back' &&
              (mine(row) || canAssign || !row.assignee)
            "
            :label="t('RELAY_VOICE.MISSED.MARK_CALLED_BACK')"
            icon="i-ph-check-bold"
            size="xs"
            variant="faded"
            color="teal"
            :is-loading="working === `${row.call_id}:called_back`"
            @click="act(row, 'called_back')"
          />
          <Button
            v-if="row.status === 'claimed' && (mine(row) || canAssign)"
            :label="t('RELAY_VOICE.MISSED.RELEASE')"
            size="xs"
            variant="ghost"
            color="slate"
            @click="act(row, 'release')"
          />
          <Button
            v-if="row.status === 'called_back' && canAssign"
            :label="t('RELAY_VOICE.MISSED.REOPEN')"
            size="xs"
            variant="ghost"
            color="slate"
            @click="act(row, 'reopen')"
          />
          <label
            v-if="canAssign && row.status !== 'called_back'"
            class="flex items-center gap-2 text-xs text-n-slate-11 ms-auto"
          >
            {{ t('RELAY_VOICE.MISSED.ASSIGN') }}
            <select
              :value="row.assignee?.id || ''"
              class="!mb-0 rounded-lg border border-n-weak bg-n-alpha-1 px-2 py-1 text-xs text-n-slate-12"
              @change="
                event =>
                  event.target.value &&
                  act(row, 'assign', { user_id: Number(event.target.value) })
              "
            >
              <option value="">{{ t('RELAY_VOICE.MISSED.NOBODY') }}</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
          </label>
        </div>
      </li>
    </ul>

    <PaginationFooter
      v-if="total > PER_PAGE"
      v-model:current-page="page"
      :total-items="total"
      :items-per-page="PER_PAGE"
    />
  </section>
</template>
